import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ClientSidebar from '../../components/ClientSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { clientService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Edit, Save, X, Eye, EyeOff, Briefcase, Euro, Menu } from 'lucide-react';

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'salarie', label: 'Salarié(e)' },
  { value: 'retraite', label: 'Retraité(e)' },
  { value: 'independant', label: 'Indépendant / Auto-entrepreneur' },
  { value: 'chomeur', label: 'Demandeur d\'emploi' },
  { value: 'etudiant', label: 'Étudiant(e)' },
  { value: 'autre', label: 'Autre' }
];

const PersonalData = () => {
  const { user, updateUser, updatePassword } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPro, setIsEditingPro] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    zipCode: user?.zipCode || ''
  });

  const [proData, setProData] = useState({
    employmentStatus: user?.employmentStatus || '',
    employer: user?.employer || '',
    monthlyIncome: user?.monthlyIncome || '',
    employmentStartDate: user?.employmentStartDate || ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        zipCode: user.zipCode || ''
      });
      setProData({
        employmentStatus: user.employmentStatus || '',
        employer: user.employer || '',
        monthlyIncome: user.monthlyIncome || '',
        employmentStartDate: user.employmentStartDate || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedClient = await clientService.update(user.id, formData);
      updateUser(updatedClient);
      toast({
        title: 'Données mises à jour',
        description: 'Vos informations personnelles ont été enregistrées.'
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour vos informations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePro = async () => {
    setLoading(true);
    try {
      const updateData = {
        employmentStatus: proData.employmentStatus,
        employer: proData.employer,
        monthlyIncome: proData.monthlyIncome ? parseFloat(proData.monthlyIncome) : null,
        employmentStartDate: proData.employmentStartDate
      };
      const updatedClient = await clientService.update(user.id, updateData);
      updateUser(updatedClient);
      toast({
        title: 'Données mises à jour',
        description: 'Vos informations professionnelles ont été enregistrées.'
      });
      setIsEditingPro(false);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour vos informations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        variant: 'destructive'
      });
      return;
    }

    if (passwords.new.length < 6) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 6 caractères',
        variant: 'destructive'
      });
      return;
    }

    updatePassword(passwords.new);
    toast({
      title: 'Mot de passe modifié',
      description: 'Votre mot de passe a été mis à jour avec succès. L\'administrateur a été notifié.'
    });
    setPasswords({ current: '', new: '', confirm: '' });
    setShowPasswordForm(false);
  };

  const getStatusLabel = (status) => {
    const option = EMPLOYMENT_STATUS_OPTIONS.find(o => o.value === status);
    return option ? option.label : status || 'Non renseigné';
  };

  return (
    <div className="flex min-h-screen">
      <ClientSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex-1 bg-gray-50">
        {/* Mobile Header - Black to match logo */}
        <div className="md:hidden bg-black text-white p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/client/dashboard">
            <img 
              src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
              alt="Younited"
              className="h-8 object-contain"
            />
          </Link>
          <div className="w-10"></div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block bg-gray-800 text-white py-3 px-8 text-sm">
          Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
        </div>

        {/* Banner Alert */}
        <div className="bg-yellow-50 border-b border-yellow-200 py-2 md:py-3 px-4 md:px-8">
          <div className="flex items-center space-x-2 text-xs md:text-sm text-yellow-800">
            <span>⚠️</span>
            <span className="line-clamp-2 md:line-clamp-1">
              Pour modifier vos coordonnées personnelles ou bancaires, téléchargez l'application mobile Younited en activant Sécurité. En savoir plus
            </span>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto p-4 md:p-8">
          <Tabs defaultValue="personal" className="space-y-6">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              <TabsTrigger value="personal" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 text-xs md:text-sm">
                Mes coordonnées
              </TabsTrigger>
              <TabsTrigger value="professional" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 text-xs md:text-sm">
                Situation pro.
              </TabsTrigger>
            </TabsList>

            <TabsContent value="personal">
              <Card className="p-4 md:p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">Informations personnelles</h2>
                  {!isEditing ? (
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center text-sm"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(false)}
                        className="flex items-center text-sm"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSave}
                        className="flex items-center text-white text-sm"
                        style={{ backgroundColor: '#8B5CF6' }}
                        disabled={loading}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4 md:space-y-6">
                  <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <Label className="text-sm">Prénom</Label>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Nom</Label>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      disabled={true}
                      className="mt-1 bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
                  </div>

                  <div>
                    <Label className="text-sm">Téléphone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Adresse postale</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <Label className="text-sm">Code postal</Label>
                      <Input
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Ville</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Change Password Section */}
                <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-gray-200">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Sécurité du compte</h3>
                  {!showPasswordForm ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowPasswordForm(true)}
                      className="text-sm"
                    >
                      Changer le mot de passe
                    </Button>
                  ) : (
                    <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                      <div>
                        <Label className="text-sm">Mot de passe actuel</Label>
                        <div className="relative mt-1">
                          <Input
                            type={showPasswords.current ? 'text' : 'password'}
                            value={passwords.current}
                            onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            className="absolute right-3 top-3 text-gray-400"
                          >
                            {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Nouveau mot de passe</Label>
                        <div className="relative mt-1">
                          <Input
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwords.new}
                            onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            className="absolute right-3 top-3 text-gray-400"
                          >
                            {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Confirmer le mot de passe</Label>
                        <div className="relative mt-1">
                          <Input
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwords.confirm}
                            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            className="absolute right-3 top-3 text-gray-400"
                          >
                            {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button type="submit" className="text-white text-sm" style={{ backgroundColor: '#8B5CF6' }}>
                          Mettre à jour
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowPasswordForm(false);
                            setPasswords({ current: '', new: '', confirm: '' });
                          }}
                          className="text-sm"
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="professional">
              <Card className="p-4 md:p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Briefcase className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
                    </div>
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">Situation professionnelle</h2>
                  </div>
                  {!isEditingPro ? (
                    <Button
                      variant="outline"
                      onClick={() => setIsEditingPro(true)}
                      className="flex items-center text-sm"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsEditingPro(false)}
                        className="flex items-center text-sm"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSavePro}
                        className="flex items-center text-white text-sm"
                        style={{ backgroundColor: '#8B5CF6' }}
                        disabled={loading}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4 md:space-y-6">
                  {/* Employment Status */}
                  <div>
                    <Label className="text-sm">Statut professionnel</Label>
                    {isEditingPro ? (
                      <Select
                        value={proData.employmentStatus}
                        onValueChange={(value) => setProData({ ...proData, employmentStatus: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionnez votre statut" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="mt-1 p-3 bg-gray-50 rounded-md">
                        <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                          proData.employmentStatus === 'salarie' ? 'bg-green-100 text-green-700' :
                          proData.employmentStatus === 'retraite' ? 'bg-blue-100 text-blue-700' :
                          proData.employmentStatus === 'independant' ? 'bg-purple-100 text-purple-700' :
                          proData.employmentStatus === 'chomeur' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {getStatusLabel(proData.employmentStatus)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Employer (only for employees) */}
                  {(proData.employmentStatus === 'salarie' || proData.employmentStatus === 'independant' || isEditingPro) && (
                    <div>
                      <Label className="text-sm">
                        {proData.employmentStatus === 'independant' ? 'Nom de l\'entreprise' : 'Employeur'}
                      </Label>
                      <Input
                        value={proData.employer}
                        onChange={(e) => setProData({ ...proData, employer: e.target.value })}
                        disabled={!isEditingPro}
                        placeholder={proData.employmentStatus === 'independant' ? 'Nom de votre entreprise' : 'Nom de votre employeur'}
                        className="mt-1"
                      />
                    </div>
                  )}

                  {/* Monthly Income */}
                  <div>
                    <Label className="flex items-center space-x-2 text-sm">
                      <Euro className="h-4 w-4" />
                      <span>Revenus mensuels nets</span>
                    </Label>
                    {isEditingPro ? (
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={proData.monthlyIncome}
                          onChange={(e) => setProData({ ...proData, monthlyIncome: e.target.value })}
                          placeholder="Ex: 2500"
                          className="pr-10"
                        />
                        <span className="absolute right-3 top-3 text-gray-500">€</span>
                      </div>
                    ) : (
                      <div className="mt-1 p-3 bg-gray-50 rounded-md">
                        <span className="text-base md:text-lg font-semibold text-gray-900">
                          {proData.monthlyIncome ? `${parseFloat(proData.monthlyIncome).toLocaleString()} €` : 'Non renseigné'}
                        </span>
                        <span className="text-xs md:text-sm text-gray-500 ml-2">/ mois</span>
                      </div>
                    )}
                  </div>

                  {/* Employment Start Date */}
                  {(proData.employmentStatus === 'salarie' || isEditingPro) && (
                    <div>
                      <Label className="text-sm">Date de début d'emploi</Label>
                      <Input
                        type="date"
                        value={proData.employmentStartDate}
                        onChange={(e) => setProData({ ...proData, employmentStartDate: e.target.value })}
                        disabled={!isEditingPro}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="mt-6 md:mt-8 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs md:text-sm text-purple-800">
                    <strong>Pourquoi ces informations ?</strong><br />
                    Ces informations sont nécessaires pour évaluer votre capacité de remboursement et vous proposer des offres adaptées à votre situation.
                  </p>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
